// src/@crema/utility/hooks/useAbility.js
import {useMemo} from 'react';
import {useJWTAuth} from '@crema/services/auth/jwt-auth/JWTAuthProvider';

export const useAbility = () => {
  const {
    // authoritative from backend
    features = {},
    featuresList = [],
    permissions = [],
    // status
    isLoading,
  } = useJWTAuth() || {};

  const {featureSet, permSet} = useMemo(() => {
    const enabledCodes = new Set(
      (Array.isArray(featuresList) ? featuresList : [])
        .filter((f) => f && f.code && (f.enabled === true || f.enabled === 1))
        .map((f) => f.code),
    );

    if (enabledCodes.size === 0 && features && typeof features === 'object') {
      Object.entries(features).forEach(([code, val]) => {
        const origin = val?.origin || val?.scope; // e.g., 'user' | 'package' | 'business'
        const enabled =
          val === true ||
          val?.enabled === true ||
          val?.enabled === 1 ||
          val?.enabled === '1' ||
          val?.enabled === 'true';
        if (enabled && code && (origin === 'user' || origin === 'package')) {
          enabledCodes.add(code);
        }
      });
    }

    return {
      featureSet: enabledCodes,
      permSet: new Set(Array.isArray(permissions) ? permissions : []),
    };
  }, [featuresList, features, permissions]);

  const api = useMemo(() => {
    const hasWildcardPerm = permSet.has('*');
    const hasFeature = (code) => !!code && featureSet.has(code);
    const hasAnyFeature = (codes = []) => codes.some((c) => featureSet.has(c));
    const hasAllFeatures = (codes = []) =>
      codes.every((c) => featureSet.has(c));
    const can = (perm) => !!perm && (hasWildcardPerm || permSet.has(perm));
    const canAny = (perms = []) =>
      hasWildcardPerm || perms.some((p) => permSet.has(p));

    return {
      hasFeature,
      hasAnyFeature,
      hasAllFeatures,
      can,
      canAny,
      hasWildcardPerm,
    };
  }, [featureSet, permSet]);

  return {isLoading: !!isLoading, featureSet, permSet, ...api};
};

export default useAbility;
